import React, { useRef, useState, useEffect } from "react";
import { Grid, Paper, Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CloseIcon from "@mui/icons-material/Close";
import OrderDetailsFetcher from "./OrderDetails/OrderData/OrderDetailsFetcher";
import ProductBrowser from "../tables/OrderDetails/ProductBrowser/ProductBrowesrMain";
import InvoiceCreator from "./OrderDetails/InvoiceCreator/InvoiceCreatorMain";


const TableModalContent = ({
  type,
  tableId,
  rootId,
  onSuccess,
  onClose,
  onOrderDeleted,
  orderId,
}) => {
  const reloadDetailsRef = useRef(null);
  const navigate = useNavigate();
  const scrollBoxRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // new: bottom sheet state for mobile
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  useEffect(() => {
    // when opening a different order, collapse details by default on mobile
    setDetailsExpanded(false);
  }, [orderId]);

  const handleScrollDown = () => {
    if (scrollBoxRef.current) {
      scrollBoxRef.current.scrollTop = scrollBoxRef.current.scrollHeight;
    }
  };

  // ...existing handlers...

  if (isMobile) {
    // Mobile-first layout: ProductBrowser above, details in bottom-sheet
    return (
      <Box dir="rtl" sx={{ width: "100%", height: "100%", position: "relative" }}>
        {/* Main product area: full screen scrollable content */}
        <Box sx={{ pb: 14, px: 1 }}>
          {type === "addItem" && orderId && (
            <Box sx={{ mt: 1 }}>
              <ProductBrowser
                orderId={orderId}
                onSuccess={() => {
                  if (typeof reloadDetailsRef.current === "function") {
                    reloadDetailsRef.current();
                  }
                }}
              />
            </Box>
          )}
        </Box>

        {/* Bottom-sheet for order details */}
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1600,
            display: "flex",
            justifyContent: "center",
            p: 1,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              width: "100%",
              maxWidth: 720,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              overflow: "hidden",
              bgcolor: "#1e1e2f",
              color: "#fff",
              // collapsed vs expanded height
              height: detailsExpanded ? "72vh" : "92px",
              transition: "height 220ms ease",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Handle / header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1,
                borderBottom: "1px solid rgba(255,255,255,0.04)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 8 }} /> {/* spacing */}
                <Box sx={{ fontWeight: 800, fontSize: 15 }}>
                  {orderId ? `طلب ${orderId}` : "تفاصيل الطلب"}
                </Box>
                <Box sx={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>تفاصيل سريعة</Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton
                  onClick={() => {
                    setDetailsExpanded((s) => !s);
                    // if expanding, optionally scroll to bottom content after a tick
                    if (!detailsExpanded) setTimeout(() => handleScrollDown(), 300);
                  }}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.06)",
                    color: "#fff",
                    width: 40,
                    height: 40,
                  }}
                >
                  {detailsExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                </IconButton>

                <IconButton
                  onClick={() => {
                    if (onClose) onClose();
                  }}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.06)",
                    color: "#fff",
                    width: 40,
                    height: 40,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Content area */}
            <Box
              ref={scrollBoxRef}
              sx={{
                flex: 1,
                overflowY: "auto",
                px: 2,
                py: 1,
                // hide native scrollbar for cleaner mobile look
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {orderId ? (
                <>
                  <OrderDetailsFetcher
                    orderId={orderId}
                    onReload={(fn) => (reloadDetailsRef.current = fn)}
                    onClose={onClose}
                    onOrderDeleted={onOrderDeleted}
                  />

                  <Box sx={{ mt: 2 }}>
                    <InvoiceCreator
                      orderId={orderId}
                      onSuccess={() => {
                        if (onSuccess) onSuccess();
                        if (onClose) onClose();
                        navigate("/pos");
                      }}
                    />
                  </Box>
                </>
              ) : (
                <Box sx={{ py: 2, textAlign: "center", color: "rgba(255,255,255,0.7)" }}>
                  لا يوجد طلب محدد
                </Box>
              )}
            </Box>

            {/* Scroll-down floating button (visible when expanded) */}
            {detailsExpanded && (
              <Box
                sx={{
                  position: "absolute",
                  left: 12,
                  bottom: 12,
                }}
              >
                <IconButton
                  size="large"
                  color="primary"
                  onClick={handleScrollDown}
                  sx={{
                    bgcolor: "#fbc2eb",
                    color: "#232334",
                    "&:hover": { bgcolor: "#ff9a9e" },
                    width: 44,
                    height: 44,
                  }}
                >
                  <KeyboardArrowDownIcon />
                </IconButton>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    );
  }

  // Desktop / tablet: keep two-column layout (similar to original) but with improved spacing
  return (
    <Grid
      container
      spacing={{ xs: 1, sm: 2 }}
      sx={{
        flexDirection: { xs: "column-reverse", md: "row" },
        alignItems: "stretch",
      }}
    >
      {/* Left Section - Product Browser */}
      <Grid
        item
        xs={12}
        md={7}
        sx={{
          order: { xs: 2, md: 1 },
          mb: { xs: 2, md: 0 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        {type === "addItem" && orderId && (
          <ProductBrowser
            orderId={orderId}
            onSuccess={() => {
              if (typeof reloadDetailsRef.current === "function") {
                reloadDetailsRef.current();
              }
            }}
          />
        )}
      </Grid>

      {/* Right Section - Order Details */}
      <Grid
        item
        xs={12}
        md={5}
        sx={{
          order: { xs: 1, md: 2 },
          mb: { xs: 2, md: 0 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          sx={{
            p: { xs: 1, sm: 2 },
            bgcolor: "#1e1e2f",
            color: "#fff",
            height: "100%",
            minHeight: { xs: "auto", md: "400px" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            ref={scrollBoxRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              minHeight: 0,
              maxHeight: { xs: 600, md: "calc(100vh - 100px)" },
              pr: 1,
              // إخفاء شريط الاسكرول
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {orderId && (
              <>
                <OrderDetailsFetcher
                  orderId={orderId}
                  onReload={(fn) => (reloadDetailsRef.current = fn)}
                  onClose={onClose}
                  onOrderDeleted={onOrderDeleted}
                />

                <InvoiceCreator
                  orderId={orderId}
                  onSuccess={() => {
                    if (onSuccess) onSuccess();
                    if (onClose) onClose();
                    navigate("/tables");
                  }}
                />
              </>
            )}
          </Box>
          {/* زر النزول للأسفل */}
          <Box
            sx={{
              position: "absolute",
              left: 10,
              bottom: 10,
              zIndex: 10,
              bgcolor: "#232334",
              borderRadius: "50%",
              boxShadow: 2,
            }}
          >
            <IconButton
              size="large"
              color="primary"
              onClick={handleScrollDown}
              sx={{
                bgcolor: "#fbc2eb",
                color: "#232334",
                "&:hover": { bgcolor: "#ff9a9e" },
                width: 40,
                height: 40,
              }}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default TableModalContent;

